import { AlertTriangle, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { RecoveryStatus } from '@/services/recoveryApi';

interface RecoveryBannerProps {
  recoveryStatus: RecoveryStatus;
  onStartRecovery: () => void;
  isLoading?: boolean;
}

/**
 * Banner displayed when a user's bio or learning path is incomplete.
 * Shows appropriate message and CTA based on what's missing.
 */
export function RecoveryBanner({
  recoveryStatus,
  onStartRecovery,
  isLoading = false,
}: RecoveryBannerProps) {
  const { recoveryActions, hasSurveyData } = recoveryStatus;

  if (!recoveryActions || recoveryActions.length === 0) return null;

  const needsSurvey = recoveryActions.includes('NEEDS_SURVEY');
  const needsBio = recoveryActions.includes('NEEDS_BIO');
  const needsLearningPath = recoveryActions.includes('NEEDS_LEARNING_PATH');

  // Build descriptive message
  const missingItems: string[] = [];
  if (needsBio) missingItems.push('AI bio');
  if (needsLearningPath) missingItems.push('learning plan');

  const message = missingItems.length > 0
    ? `Your ${missingItems.join(' and ')} ${missingItems.length > 1 ? 'are' : 'is'} not generated yet.`
    : 'Your profile needs attention.';

  const ctaText = needsSurvey
    ? 'Complete Quick Survey & Generate'
    : hasSurveyData
      ? 'Generate Now'
      : 'Complete Quick Survey & Generate';

  return (
    <div className="rounded-xl border border-amber-200/60 bg-amber-50/50 dark:border-amber-800/40 dark:bg-amber-950/20 p-4 mb-6">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {message}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            {needsSurvey
              ? 'Answer a few quick questions so we can create your personalized content.'
              : 'We have your survey data — click below to generate your personalized content.'}
          </p>
        </div>
        <Button
          size="sm"
          onClick={onStartRecovery}
          disabled={isLoading}
          className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-4 w-4" />
          )}
          {isLoading ? 'Generating...' : ctaText}
        </Button>
      </div>
    </div>
  );
}
